import { getAIConversationPendingAnswers } from '../../../../../api'
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../../../constants/common'
import { hasAIConversationPendingAnswers } from '../../../../../utils/aiAgents'
import { GRETA_AI_CHAT_DEFERRED_CHECK_TIMEOUT_MS } from '../useGretaAIChatReplyRequest.constants'
import {
  appendGretaAIChatAssistantReply,
  parseGretaAIChatReply,
} from '../useGretaAIChatReplyRequest.utils'

// A completion that came back: delivered, a foreign stop, or a reply that isn't an answer.
export function useGretaAIChatReplyResponse({
  checkGenerationSettled,
  endReplySettle,
  getIsOpenConversation,
  setMessages,
  setStatus,
  startReplySettle,
  syncDeliveredTurn,
  trackDeliveredTurn,
  waitForRunningTurn,
  onGenerationSettled = EMPTY_FUNCTION,
}) {
  function checkDeferredAnswer(conversationId) {
    const options = { timeout: GRETA_AI_CHAT_DEFERRED_CHECK_TIMEOUT_MS }

    return getAIConversationPendingAnswers({
      id: conversationId,
      options,
    }).then(
      pendingAnswers =>
        hasAIConversationPendingAnswers(pendingAnswers)
          ? { pendingAnswers }
          : null,
      async () =>
        (await checkGenerationSettled(conversationId, options))
          ? null
          : { pendingAnswers: null }
    )
  }

  async function handleReply({ conversationId, payloadMessages, response }) {
    if (!response) {
      return EMPTY_OBJECT
    }

    const { delivered, isForeignStop, reply, replyStatus } =
      parseGretaAIChatReply(response)

    if (isForeignStop) {
      await waitForRunningTurn(conversationId)

      return { shouldClearTurnStart: false }
    }

    if (!delivered) {
      onGenerationSettled(conversationId)

      if (getIsOpenConversation(conversationId)) {
        setStatus(replyStatus)
      }

      return EMPTY_OBJECT
    }

    // Idle with the reply; the kept turn holds Send until the check settles.
    startReplySettle(conversationId)
    trackDeliveredTurn(conversationId, payloadMessages)

    if (getIsOpenConversation(conversationId)) {
      setMessages(current => appendGretaAIChatAssistantReply(current, reply))
      setStatus(replyStatus)
    }

    const deferredAnswer = await checkDeferredAnswer(conversationId)

    if (deferredAnswer) {
      endReplySettle(conversationId)
      await waitForRunningTurn(conversationId, deferredAnswer)

      return { delivered, shouldClearTurnStart: false }
    }

    onGenerationSettled(conversationId)

    syncDeliveredTurn(conversationId)

    return { delivered }
  }

  return { handleReply }
}
